/**
 * <vsr-finder> — the repair configurator. Four questions resolve to one unit,
 * one part number, one price. The catalogue is one JSON payload from Liquid,
 * never scraped from the DOM; steps 1-3 are server-rendered and only shown or
 * hidden, so they stay crawlable; values are set with `textContent`, so
 * merchant copy can never become markup.
 *
 * HARD CEILING: AssetSizeJavaScript is 20480 B and is an error, so the design
 * rationale lives in sections/screen-finder.liquid and
 * assets/component-screen-finder.css, which have none. Comment weight here is
 * the budget — earn room before spending any.
 */

(function () {
  'use strict';

  /** Step keys in order. Index + 1 is the step number used in the markup. */
  const STEPS = ['issue', 'brand', 'model', 'year'];

  /**
   * What each answer invalidates. A graph, NOT "everything after this step":
   * the fault is independent of the vehicle, and clearing a vehicle to price a
   * second fault is what makes a visitor give up.
   */
  const DEPENDENTS = {
    issue: [],
    brand: ['model', 'year'],
    model: ['year'],
    year: []
  };
  const RESULT_STEP = STEPS.length + 1;

  /** Long enough for the selected state to register, short enough to feel
   *  instant. */
  const ADVANCE_DELAY = 260;

  /** The calculating panel's run time: the stylesheet's 2900ms bar plus a held
   *  beat. Change one, change the other. Why it exists is above its markup. */
  const CALC_MS = 3100;

  /** Reads a JSON <script> child, tolerating an empty or malformed payload. */
  function readCatalog(root) {
    const node = root.querySelector('[data-finder-catalog]');
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      console.warn('[vsr-finder] catalog is not valid JSON', error);
      return null;
    }
  }

  function toYear(value) {
    const year = parseInt(value, 10);
    return Number.isFinite(year) ? year : null;
  }

  class VsrFinder extends HTMLElement {
    connectedCallback() {
      this.catalog = readCatalog(this);
      if (!this.catalog || !Array.isArray(this.catalog.fitments)) return;

      this.controller = new AbortController();
      const { signal } = this.controller;

      this.reduced = Boolean(window.VSR && window.VSR.prefersReducedMotion());
      this.live = this.querySelector('[data-finder-live]');

      this.stepNodes = new Map();
      this.querySelectorAll('[data-step]').forEach((node) => {
        this.stepNodes.set(Number(node.dataset.step), node);
      });
      this.resultNode = this.stepNodes.get(RESULT_STEP);

      this.yearPanel = this.querySelector('[data-year-panel]');
      this.yearInput = this.querySelector('[data-year-input]');
      this.yearReadout = this.querySelector('[data-year-readout]');
      this.yearScale = this.querySelector('[data-year-scale]');
      this.progressButtons = Array.from(this.querySelectorAll('[data-goto]'));
      // A list, not a Map: one key may print in two places. Why, in the Liquid.
      this.progressValues = Array.from(this.querySelectorAll('[data-progress-value]'));

      this.selection = { issue: null, brand: null, model: null, year: null };
      this.suppliedPart = null;
      // Which question the visitor could not answer. Set only by the "not
      // listed" tile; every other move clears it.
      this.unknown = null;
      this.current = 1;
      this.advanceTimer = 0;
      // Armed by an answer landing on the result, consumed once on arrival.
      this.calcOn = false;
      this.calcTimer = 0;

      this.addEventListener('click', (event) => this.onClick(event), { signal });
      this.addEventListener('keydown', (event) => this.onKeydown(event), { signal });
      this.yearInput?.addEventListener('input', () => this.paintYear(), { signal });

      this.render();
    }

    disconnectedCallback() {
      window.clearTimeout(this.advanceTimer);
      window.clearTimeout(this.calcTimer);
      this.controller?.abort();
    }

    /* ---- Catalogue queries ---- */

    get fitments() {
      return this.catalog.fitments;
    }

    issueById(id) {
      return (this.catalog.issues || []).find((issue) => issue.id === id) || null;
    }

    /** Every fitment for the chosen brand + model. */
    fitmentsForModel() {
      const { brand, model } = this.selection;
      if (!brand || !model) return [];
      return this.fitments.filter((f) => f.brand === brand && f.model === model);
    }

    /** Union of the model's ranges; an open range runs to the current year. */
    yearsForModel() {
      const years = new Set();
      const thisYear = new Date().getFullYear();

      this.fitmentsForModel().forEach((fitment) => {
        const from = toYear(fitment.yearFrom);
        if (from === null) return;
        const to = toYear(fitment.yearTo) ?? thisYear;
        for (let year = from; year <= Math.max(from, to); year += 1) years.add(year);
      });

      return Array.from(years).sort((a, b) => b - a);
    }

    /** The single fitment that covers the chosen model and year. */
    resolveFitment() {
      const year = toYear(this.selection.year);
      if (year === null) return null;
      const thisYear = new Date().getFullYear();

      return (
        this.fitmentsForModel().find((fitment) => {
          const from = toYear(fitment.yearFrom);
          if (from === null) return false;
          const to = toYear(fitment.yearTo) ?? thisYear;
          return year >= from && year <= Math.max(from, to);
        }) || null
      );
    }

    /* ---- Interaction ---- */

    onClick(event) {
      const hit = (selector) => {
        const el = event.target.closest(selector);
        return el && this.contains(el) ? el : null;
      };

      const choice = hit('[data-choice]');
      if (choice) return this.choose(choice.dataset.choice, choice.dataset.value);

      if (hit('[data-year-confirm]')) return this.commitYear();

      const out = hit('[data-finder-escape]');
      if (out) return this.escape(out.dataset.finderEscape);

      const back = hit('[data-finder-back]');
      if (back) return this.goTo(Math.max(1, this.current - 1), true);

      const goto = hit('[data-goto]');
      if (goto && !goto.disabled) return this.goTo(Number(goto.dataset.goto), true);

      const restart = hit('[data-finder-restart]');
      if (restart) this.restart();
    }

    onKeydown(event) {
      if (event.key !== 'Escape') return;
      if (this.current === 1 && !this.selection.issue) return;
      event.preventDefault();
      this.restart();
    }

    /** Clear an answer and everything downstream of it. */
    clear(key) {
      this.selection[key] = null;
      (DEPENDENTS[key] || []).forEach((dependent) => {
        this.selection[dependent] = null;
      });
    }

    /** Record an answer, clear its dependents, move to the first unanswered
     *  question. */
    choose(key, value) {
      if (!(key in this.selection)) return;

      this.unknown = null;
      this.clear(key);
      this.selection[key] = value;

      this.render();

      const pending = STEPS.findIndex((step) => !this.selection[step]);
      const next = pending === -1 ? RESULT_STEP : pending + 1;

      // Only an ANSWER earns the sequence; the out and the rail do not.
      this.calcOn = next === RESULT_STEP && !this.reduced;

      window.clearTimeout(this.advanceTimer);
      const advance = () => this.goTo(next, true);
      if (this.reduced) advance();
      else this.advanceTimer = window.setTimeout(advance, ADVANCE_DELAY);
    }

    /**
     * The out — rationale in the Liquid header. Sets `current` directly, not
     * through `goTo`, which refuses to pass an unanswered question.
     */
    escape(key) {
      if (!(key in this.selection)) return;
      window.clearTimeout(this.advanceTimer);

      this.clear(key);
      this.unknown = key;
      // Armed by an ANSWER only. Taking the out is not an answer, and leaving
      // it armed runs the calculating sequence over a card with nothing to
      // calculate — three seconds of theatre before "we could not identify it".
      this.calcOn = false;
      this.current = RESULT_STEP;
      this.render(true);
    }

    goTo(step, moveFocus) {
      // A pending auto-advance outlives the navigation that replaces it: tap
      // Back inside the 260 ms window and the timer still fires, throwing the
      // visitor forward again. `escape()` has always cleared it; this did not.
      window.clearTimeout(this.advanceTimer);
      this.unknown = null;
      const target = Math.min(Math.max(step, 1), RESULT_STEP);

      // Never advance past an unanswered question.
      for (let i = 0; i < Math.min(target, RESULT_STEP) - 1; i += 1) {
        if (!this.selection[STEPS[i]]) {
          this.current = i + 1;
          this.render(moveFocus);
          return;
        }
      }

      this.current = target;
      this.render(moveFocus);
    }

    restart() {
      window.clearTimeout(this.advanceTimer);
      this.stopCalc();
      this.unknown = null;
      this.suppliedPart = null;
      this.querySelectorAll('vsr-finder-lookup').forEach((lookup) => lookup.reset?.());
      STEPS.forEach((key) => this.clear(key));
      this.current = 1;
      this.render(true);
      this.announce(this.string('restarted'));
    }

    /* ---- Rendering ---- */

    render(moveFocus) {
      this.renderModels();
      this.renderYears();
      this.renderSelectedStates();
      this.renderProgress();
      this.renderResult();
      this.showStep(moveFocus);
    }

    /** Step 3 lists every model in the DOM; hide the ones off-brand. */
    renderModels() {
      const brand = this.selection.brand;
      this.querySelectorAll('[data-model-brand]').forEach((item) => {
        item.hidden = Boolean(brand) && item.dataset.modelBrand !== brand;
      });
    }

    /** The slider's own values are years, so it announces itself. */
    renderYears() {
      if (!this.yearInput) return;
      const years = this.selection.model ? this.yearsForModel() : [];
      const newest = years[0];
      const oldest = years[years.length - 1];

      // Re-measure only on a change, or a re-render mid-drag shoves the thumb
      // back to the default.
      // Keyed on the MODEL as well. Two models sharing a year span produce an
      // identical signature, so switching between them skipped the re-measure
      // and left the slider showing the previous model's committed value.
      const signature = `${this.selection.model}:${oldest}-${newest}`;
      if (this.yearSignature !== signature) {
        this.yearSignature = signature;
        this.yearInput.min = String(oldest || 0);
        this.yearInput.max = String(newest || 0);
        // Newest: the likelier car, and the end of the track the eye lands on.
        this.yearInput.value = String(newest || 0);
        if (this.yearPanel) this.yearPanel.hidden = years.length === 0;
        // A one-year model reads "2016", not "2016–2016".
        const scale = oldest === newest ? `${oldest}` : `${oldest}–${newest}`;
        if (this.yearScale) this.yearScale.textContent = years.length ? scale : '';
      }

      if (this.selection.year) this.yearInput.value = String(this.selection.year);
      this.paintYear();
    }

    paintYear() {
      const min = Number(this.yearInput.min);
      const value = Number(this.yearInput.value);
      if (this.yearReadout) this.yearReadout.textContent = String(value);
      const span = Math.max(1, Number(this.yearInput.max) - min);
      this.yearInput.style.setProperty('--vsr-year-fill', `${((value - min) / span) * 100}%`);
    }

    /**
     * Dragging picks; only this commits. A model covered by two ranges with a
     * break has years on the track resolving to no fitment — the out.
     */
    commitYear() {
      const year = toYear(this.yearInput.value);
      if (year === null) return;
      if (this.yearsForModel().includes(year)) this.choose('year', String(year));
      else this.escape('year');
    }

    renderSelectedStates() {
      this.querySelectorAll('[data-choice]').forEach((button) => {
        const chosen = this.selection[button.dataset.choice] === button.dataset.value;
        button.setAttribute('aria-pressed', chosen ? 'true' : 'false');
        button.classList.toggle('is-selected', chosen);
      });
    }

    renderProgress() {
      this.progressButtons.forEach((button) => {
        const step = Number(button.dataset.goto);
        const answeredBefore = STEPS.slice(0, step - 1).every((key) => this.selection[key]);
        button.disabled = !answeredBefore;
        button.setAttribute('aria-current', step === this.current ? 'step' : 'false');
        button
          .closest('.vsr-finder__progress-item')
          ?.classList.toggle('is-done', Boolean(this.selection[STEPS[step - 1]]));
      });

      this.progressValues.forEach((node) => {
        node.textContent = this.labelFor(node.dataset.progressValue);
      });

      this.revealCurrentStepInRail();
    }

    /**
     * The rail needs 632px, so at 390px the route back scrolls out of sight by
     * question 3 unless followed. `scrollLeft`, not `scrollIntoView`, which
     * walks every scrollable ancestor and drags the page.
     */
    revealCurrentStepInRail() {
      const button = this.progressButtons[this.current - 1];
      const list = button?.closest('.vsr-finder__progress-list');
      if (!list || list.scrollWidth <= list.clientWidth) return;

      // Rects, not `offsetLeft`: offsets resolve against the nearest
      // POSITIONED ancestor and the rail is not positioned.
      const item = button.closest('.vsr-finder__progress-item') || button;
      const box = item.getBoundingClientRect();
      const frame = list.getBoundingClientRect();
      const viewStart = list.scrollLeft;
      const viewEnd = viewStart + list.clientWidth;
      const start = viewStart + box.left - frame.left;
      const end = start + box.width;
      if (start >= viewStart && end <= viewEnd) return;

      list.scrollLeft = start < viewStart ? start : end - list.clientWidth;
    }

    /** Human-readable value for a recorded answer. */
    labelFor(key) {
      const value = this.selection[key];
      if (!value) return '';
      if (key === 'issue') return this.issueById(value)?.label || '';
      return value;
    }

    /**
     * The card is rendered by `assets/vsr-finder-result.js`, which decides what
     * a resolution is entitled to claim. Split out because this file has 25
     * bytes of headroom under AssetSizeJavaScript; the reasoning lives there.
     *
     * Optional on purpose, and there is deliberately no fallback renderer. If
     * that file is missing the card keeps the state Liquid rendered it in — the
     * enquiry ending, no price, no buy button — and the four questions still
     * work. A second implementation of the grading is a second chance to get it
     * wrong, and the wrong answer here is a repair sold against a unit nobody
     * identified.
     */
    renderResult() {
      if (this.resultNode && window.VSRFinderResult) {
        window.VSRFinderResult(this, this.resultNode);
      }
    }

    showStep(moveFocus) {
      this.stepNodes.forEach((node, step) => {
        node.hidden = step !== this.current;
      });

      const active = this.stepNodes.get(this.current);
      if (!active) return;

      if (!this.reduced) {
        active.classList.remove('is-entering');
        // Force a reflow so the animation restarts on every step change.
        void active.offsetWidth;
        active.classList.add('is-entering');
      }

      if (moveFocus) {
        // The container, not the heading: `role="group"` + `aria-labelledby`
        // announces the question either way, and draws no ring.
        active.setAttribute('tabindex', '-1');
        active.focus({ preventScroll: true });

        // A choice near the bottom of a long mobile list must reveal the next
        // question. Keep the page still when that question is already in view.
        const top = active.getBoundingClientRect().top;
        const inset = parseFloat(getComputedStyle(document.documentElement).scrollPaddingTop) || 0;
        if (top < inset || top > window.innerHeight / 2) {
          active.scrollIntoView({ block: 'start', behavior: this.reduced ? 'instant' : 'smooth' });
        }
      }

      // Announced on the REVEAL, not the arrival: while the panel runs the card
      // is `visibility: hidden` and out of the accessibility tree.
      this.stopCalc();
      if (this.current === RESULT_STEP && this.calcOn && active.querySelector('[data-calc]')) {
        this.calcOn = false;
        active.classList.add('is-calculating');
        this.calcTimer = window.setTimeout(() => this.stopCalc(true), CALC_MS);
        return;
      }

      this.announce(
        this.current === RESULT_STEP
          ? this.string('announce_result')
          : `${this.string('announce_step')} ${this.current} / ${STEPS.length}`
      );
    }

    /** Ends the sequence, early or on time; announces only if it ran to it. */
    stopCalc(announce) {
      window.clearTimeout(this.calcTimer);
      this.resultNode?.classList.remove('is-calculating');
      if (announce) this.announce(this.string('announce_result'));
    }

    /* ---- Utilities ---- */

    string(key) {
      return (this.catalog.strings && this.catalog.strings[key]) || '';
    }

    announce(message) {
      if (!this.live || !message) return;
      // Cleared first so an identical message is announced again.
      this.live.textContent = '';
      window.requestAnimationFrame(() => {
        if (this.live) this.live.textContent = message;
      });
    }
  }

  if (window.VSR) window.VSR.define('vsr-finder', VsrFinder);
  else if (!customElements.get('vsr-finder')) customElements.define('vsr-finder', VsrFinder);
})();
